import { strapiImageGetters } from './strapiImageGetters'
import type { StrapiEventListPage } from '~/composables/types/strapiEventListPage'

const getId = (eventListPage: StrapiEventListPage): number | null => {
  return eventListPage?.id || null
}

const getExternalId = (eventListPage: StrapiEventListPage): string | null => {
  return eventListPage?.external_id || null
}

const getSlug = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.slug || ''
}

const getTitle = (eventListPage: StrapiEventListPage): string | null => {
  return eventListPage?.title || null
}

const getDescription = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.description || ''
}

const getSearchQuery = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.searchQuery || null
}

const getSearchQueryObj = (eventListPage: StrapiEventListPage): any => {
  const query = {}

  const queryArr =
    eventListPage?.searchQuery?.split('&').map((x) => x.split('=')) || []
  queryArr.forEach((x) => {
    query[x[0]] = x[1]
  })

  return queryArr.length > 0 ? query : null
}

const getType = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.type || null
}

const getSeoTitle = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.seo?.metaTitle
}

const getSeoImageUrl = (
  eventListPage: StrapiEventListPage,
  format = null
): string => {
  return (
    strapiImageGetters.getImageUrl(eventListPage?.seo?.metaImage, format) ||
    null
  )
}

const getSeoMetaDescription = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.seo?.metaDescription || null
}

const getCreatedAt = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.createdAt || null
}

const getShowcase = (eventListPage: StrapiEventListPage): any => {
  return eventListPage?.showcase || null
}

const getShowcaseTitle = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.showcase?.title || null
}

const getHorizontalCardListTitle = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.horizontalCardListTitle || null
}

const getHorizontalCardListLink = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.horizontalCardListLink || null
}

const getHorizontalCardListLinkText = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.horizontalCardListLinkText || null
}

const getHorizontalCardList = (eventListPage: StrapiEventListPage): any[] => {
  return eventListPage?.horizontalCardList || []
}

const getGiftcardSection = (eventListPage: StrapiEventListPage): any => {
  return eventListPage?.giftcardSection || null
}
const getGiftcardSectionTitle = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.giftcardSection?.title || null
}

const getGiftcardSectionSectionTitle = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.giftcardSection?.sectionTitle || null
}
const getGiftcardSectionSubtitle = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.giftcardSection?.subtitle || null
}

const getCitySlug = (eventListPage: StrapiEventListPage): string | null => {
  return eventListPage?.citySlug || null
}

const getCategorySlug = (eventListPage: StrapiEventListPage): string | null => {
  return eventListPage?.categorySlug || null
}

const getLocale = (eventListPage?: StrapiEventListPage): string | null => {
  return eventListPage?.locale || null
}

const getSearchHeading = (
  eventListPage: StrapiEventListPage
): string | null => {
  return eventListPage?.searchHeading || null
}

const getSearchSubtitle = (
  eventListPage: StrapiEventListPage
): string | null => {
  return eventListPage?.searchSubtitle || null
}

const getLocalizations = (eventListPage: StrapiEventListPage): any[] => {
  return eventListPage?.localizations || null
}

const getLocalizationByLocale = (
  eventListPage: StrapiEventListPage,
  locale: string
): any => {
  if (!eventListPage) return null

  const localizations = getLocalizations(eventListPage)
  const result =
    localizations?.find((obj) => {
      const objLocale = getLocale(obj)?.toLowerCase()
      return objLocale === locale
    }) || null

  return result
}

const getSearchFilters = (eventListPage: StrapiEventListPage): any => {
  return eventListPage?.searchFilters || null
}

const getKitsSlug = (eventListPage: StrapiEventListPage): string => {
  // For kits, the slug is stored with locale suffix (e.g., "cooking_de-de")
  // We need to extract just the category part
  const fullSlug = eventListPage?.slug || ''
  if (fullSlug.includes('_')) {
    const result = fullSlug.split('_')[0]
    return result
  }
  return fullSlug
}

const getKonfettiTeameventPageSlug = (
  eventListPage: StrapiEventListPage
): string => {
  return getSlug(eventListPage)?.split('-').splice(1).join('-')
}

const getKonfettiPagePath = (eventListPage: StrapiEventListPage): string => {
  const type = getType(eventListPage)

  const map: Record<string, string | null | undefined> = {
    // Legacy page types
    city: `${getCitySlug(eventListPage)}`,
    category: `c/${getCategorySlug(eventListPage)}`,
    'city-category': `${getCitySlug(eventListPage)}/${getCategorySlug(eventListPage)}`,
    general: `t/${getSlug(eventListPage)}`,
    teamevent: `teamevent/${getKonfettiTeameventPageSlug(eventListPage)}`,
    kits: `kits/${getKitsSlug(eventListPage)}`,
    // New hierarchical location types — all use citySlug as the URL segment
    state: `${getCitySlug(eventListPage)}`,
    region: `${getCitySlug(eventListPage)}`,
    neighborhood: `${getCitySlug(eventListPage)}`,
  }

  const result = `/${map[type]}`

  return result
}

export const strapiEventListPageGetters = {
  getId,
  getExternalId,
  getSlug,
  getCitySlug,
  getCategorySlug,
  getTitle,
  getDescription,
  getSearchQuery,
  getSearchQueryObj,
  getType,
  getSeoTitle,
  getSeoImageUrl,
  getSeoMetaDescription,
  getCreatedAt,
  getShowcase,
  getShowcaseTitle,
  getHorizontalCardList,
  getGiftcardSection,
  getGiftcardSectionTitle,
  getGiftcardSectionSectionTitle,
  getGiftcardSectionSubtitle,
  getHorizontalCardListTitle,
  getHorizontalCardListLink,
  getHorizontalCardListLinkText,
  getLocale,
  getSearchHeading,
  getSearchSubtitle,
  getLocalizations,
  getLocalizationByLocale,
  getSearchFilters,
  getKonfettiPagePath,
  getKonfettiTeameventPageSlug,
  getKitsSlug,
}
